import React, { useEffect, useState } from 'react';
import { Loader2, UserCog } from 'lucide-react';
import { adminApi } from '../services/api.jsx';
import { annoncerErreur, annoncerSucces, confirmer } from '../etats';

/* ═══════════════════════════════════════════════════════════════════════
   L'INTERVENANT D'UN PROJET — coach ou mentor

   Il se choisissait seulement à la création du projet ou à l'acceptation
   de sa demande : un intervenant absent ou parti laissait le projet sans
   suite possible. Coach et mentor partagent le même rôle technique, la
   liste propose donc les deux, avec leur profil.

   Le nouvel intervenant est prévenu par le serveur ; l'ancien perd l'accès
   au projet.
   ═══════════════════════════════════════════════════════════════════════ */

export default function IntervenantDuProjet({ projet, peutModifier, onChange }) {
  const [intervenants, setIntervenants] = useState(null);
  const [choix, setChoix] = useState(projet.coach ? String(projet.coach) : '');
  const [edition, setEdition] = useState(false);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => { setChoix(projet.coach ? String(projet.coach) : ''); }, [projet.coach]);

  useEffect(() => {
    if (!edition || intervenants) return;
    adminApi.getCoaches()
      .then((d) => setIntervenants(Array.isArray(d) ? d : []))
      .catch((e) => { annoncerErreur(e?.message || 'La liste des intervenants n’a pas pu être chargée.'); setEdition(false); });
  }, [edition, intervenants]);

  const enregistrer = async () => {
    const actuel = projet.coach ? String(projet.coach) : '';
    if (choix === actuel) { setEdition(false); return; }
    if (actuel && !choix
        && !await confirmer(`Retirer ${projet.coach_display} de ce projet ? Il n’y aura plus accès.`)) return;
    setEnCours(true);
    try {
      const maj = await adminApi.updateProjet(projet.id, { coach: choix || null });
      annoncerSucces(choix ? `${maj.coach_display} est prévenu(e) : le projet lui est confié.` : 'Intervenant retiré.');
      setEdition(false);
      onChange?.(maj);
    } catch (e) {
      annoncerErreur(e?.message || 'L’intervenant n’a pas pu être changé.');
    } finally {
      setEnCours(false);
    }
  };

  if (!edition) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-controle bg-[var(--bg-main)] border border-[var(--border)] text-xs font-semibold text-[var(--text-main)]">
        <UserCog className="w-3.5 h-3.5" />
        {projet.coach_display ? `Coach / mentor : ${projet.coach_display}` : 'Aucun coach ni mentor'}
        {peutModifier && (
          <button type="button" onClick={() => setEdition(true)}
            className="ml-1 underline underline-offset-2 text-[var(--accent-texte)] hover:no-underline">
            {projet.coach_display ? 'Changer' : 'Attribuer'}
          </button>
        )}
      </span>
    );
  }

  return (
    // Sur sa propre ligne : à côté des pastilles, la liste et ses boutons
    // passaient à la ligne et étiraient les pastilles en hauteur.
    <span className="basis-full flex items-center gap-2 flex-wrap">
      <select value={choix} onChange={(e) => setChoix(e.target.value)} disabled={!intervenants || enCours}
        aria-label="Coach ou mentor du projet" className="champ !py-1.5 !text-xs font-semibold max-w-[280px]">
        {!intervenants
          ? <option value={choix}>Chargement…</option>
          : <>
              <option value="">— Aucun —</option>
              {intervenants.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.display_name} · {c.profil_display || 'Coach'}
                </option>
              ))}
            </>}
      </select>
      <button type="button" onClick={enregistrer} disabled={!intervenants || enCours}
        aria-busy={enCours || undefined} className="act act-sm">
        {enCours && <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />}
        Enregistrer
      </button>
      <button type="button" onClick={() => { setEdition(false); setChoix(projet.coach ? String(projet.coach) : ''); }}
        disabled={enCours} className="act act-sm act-tertiaire">
        Annuler
      </button>
    </span>
  );
}
